import axios from "axios";
import BigNumber from "bignumber.js";
import { BASE18, BASE9 } from "src/configs/constance";
import { getCurrentContractAddresses } from "src/configs/address";
import PancakePairContract from "src/contract-logic/logic/PancakePairContract";
import PancakeRouterContract from "src/contract-logic/logic/PancakeRouterContract";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const client = axios.create({ baseURL: "https://scoringapi.trava.finance/ticket-nft" });

export const getTravaOutFromTicket = (amountIn) => async () => {
  const { TRAVA_TOKEN_ADDRESS, NFT_TICKET_COIN } = getCurrentContractAddresses();
  const res = await PancakeRouterContract.getAmountsOut(amountIn, [NFT_TICKET_COIN, TRAVA_TOKEN_ADDRESS]);
  return BigNumber(res?.[1] || 0)
    .dividedBy(BASE18)
    .toNumber();
};

export const getTicketOutFromTrava = (amountIn) => async () => {
  const { NFT_TICKET_COIN, TRAVA_TOKEN_ADDRESS } = getCurrentContractAddresses();
  const res = await PancakeRouterContract.getAmountsOut(BigNumber(amountIn).multipliedBy(BASE18).toFixed(0), [
    TRAVA_TOKEN_ADDRESS,
    NFT_TICKET_COIN,
  ]);
  return res?.[1] || 0;
};

export const getTravaTicketIn = (amountOuts) => async () => {
  const { NFT_TICKET_COIN, TRAVA_TOKEN_ADDRESS } = getCurrentContractAddresses();
  const res = await PancakeRouterContract.getAmountsIn(BigNumber(amountOuts).multipliedBy(BASE18).toFixed(), [
    NFT_TICKET_COIN,
    TRAVA_TOKEN_ADDRESS,
  ]);
  return res?.[0] || 0;
};

export const getTravaPriceIn = (amountOuts) => async () => {
  const { NFT_TICKET_COIN, TRAVA_TOKEN_ADDRESS } = getCurrentContractAddresses();
  const res = await PancakeRouterContract.getAmountsIn(amountOuts, [TRAVA_TOKEN_ADDRESS, NFT_TICKET_COIN]);
  return BigNumber(res?.[0] || 0)
    .dividedBy(BASE18)
    .toNumber();
};

export const getPairReserves = createAsyncThunk("accountSlice/getPairReserves", async (_) => {
  const res = await PancakePairContract.getReserves();
  return {
    reserveTrava: BigNumber(res._reserve0 || 0)
      .dividedBy(BASE18)
      .toNumber(),
    reserveTicket: parseInt(res._reserve1),
  };
});

export const getLPInfo = createAsyncThunk("accountSlice/getLPInfo", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const balanceLP = await PancakePairContract.getBalanceLP(accountAddress);
  const totalSupply = await PancakePairContract.getTotalSupply();
  return {
    balanceLP: BigNumber(balanceLP).dividedBy(BASE9).toNumber(),
    totalSupply: BigNumber(totalSupply).dividedBy(BASE9).toNumber(),
  };
});

export const getDexHistory = createAsyncThunk("dexSlice/getDexHistory", async (_, thunkAPI) => {
  const timeFrame = thunkAPI.getState().dexSlice.timeframe;
  const times = {
    "5m": 300,
    "15m": 900,
    "30m": 1800,
    "1h": 3600,
    "2h": 7200,
    "4h": 14400,
    "1d": 86400,
    "1w": 604800,
    "1m": 2419200,
    "3m": 7257600,
  };
  const start_timestamp = (new Date().getTime() / 1000 - times[timeFrame]).toFixed(0);
  const inverse = Boolean(thunkAPI.getState().dexSlice.inverse);
  const res = await client
    .get("/tickets/trading-price", { params: { start_timestamp, inverse } })
    .then((res) => res.data);
  if (res.price_history) {
    return {
      price: Object.keys(res.price_history).map((key) => [parseInt(key) * 1000, res.price_history[key]]),
      delta_value: res.delta_value,
      delta_ratio: res.delta_ratio * 100,
    };
  } else {
    return {};
  }
});

const initState = {
  pairReserves: undefined,
  balanceLP: undefined,
  totalSupply: undefined,
  dexHistory: undefined,
  delta_ratio: undefined,
  delta_value: undefined,
  inverse: false,
  timeframe: "1h",
};

const dexSlice = createSlice({
  name: "dexSlice",
  initialState: initState,
  reducers: {
    updateInverse(state, action) {
      state.inverse = action.payload;
    },
    updateTimeFrame(state, action) {
      state.timeframe = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPairReserves.fulfilled, (state, action) => {
        state.pairReserves = action.payload;
      })
      .addCase(getLPInfo.fulfilled, (state, action) => {
        state.totalSupply = action.payload.totalSupply;
        state.balanceLP = action.payload.balanceLP;
      })
      .addCase(getDexHistory.fulfilled, (state, action) => {
        state.dexHistory = action.payload?.price;
        state.delta_ratio = action.payload?.delta_ratio;
        state.delta_value = action.payload?.delta_value;
      }),
});

export const { updateInverse, updateTimeFrame } = dexSlice.actions;
export default dexSlice.reducer;
